import {PlantHierarchy, PlantHierarchyNode} from '@hconnect/apiclient'
import {ControlledAutocompleteSelectOption} from '@hm/fundamental'

import {CheckboxTreeNode} from '../types/tree'

export const countryDataByCountryCode = (
  countriesData: intlTelInput.CountryData[],
  countryId?: string | null
) =>
  countryId === 'MOCK'
    ? {name: 'MOCK', iso2: 'MOCK', dialCode: 'MOCK'}
    : countriesData.find((countryData) => countryData.iso2.toUpperCase() === countryId)

export const getCountriesData = (): intlTelInput.CountryData[] =>
  window.intlTelInputGlobals?.getCountryData() ?? []

export const getCountryFullName = (countryCode: string): string => {
  const data = countryDataByCountryCode(getCountriesData(), countryCode)
  const name = data?.name ?? countryCode
  return name.replace(/\s*\(.*\)$/, '')
}

export const flattenTreeNodesToAutocompleteOptions = (
  nodes: CheckboxTreeNode[]
): ControlledAutocompleteSelectOption[] => {
  if (nodes.length === 0) return []

  const stack = [...nodes]
  const options: ControlledAutocompleteSelectOption[] = []

  while (stack.length) {
    const node = stack.pop()
    if (!node) continue

    if (node.children && node.children.length) {
      for (let i = 0; i < node.children.length; i++) {
        stack.push(node.children[i])
      }
    } else if (!node.isDisabled) {
      options.push({label: String(node.label), value: node.id})
    }
  }

  return options
}

export const findNodeInTree = (
  nodes: CheckboxTreeNode[],
  nodeId: string | number
): CheckboxTreeNode | null => {
  if (!nodes) return null

  const stack = [...nodes]
  while (stack.length > 0) {
    const node = stack.pop()
    if (!node) continue

    if (node.id === nodeId) {
      return node
    }

    if (node.children && node.children.length > 0) {
      for (let i = node.children.length - 1; i >= 0; i--) {
        stack.push(node.children[i])
      }
    }
  }

  return null
}

export const getTopLevelSelectedNodes = (nodes: CheckboxTreeNode[]): CheckboxTreeNode[] => {
  type NodeWithDepth = {node: CheckboxTreeNode; depth: number}
  const result: NodeWithDepth[] = []
  const stack: Array<NodeWithDepth> = nodes.map((node) => ({node, depth: 0}))

  while (stack.length > 0) {
    const item = stack.pop()
    if (!item) continue

    const {node, depth} = item
    const descend = () => {
      if (!node.children || node.children.length === 0) {
        return
      }

      for (let i = 0; i < node.children.length; i++) {
        stack.push({node: node.children[i], depth: depth + 1})
      }
    }

    if (node.isDisabled) {
      descend()
    } else if (node.isSelected && !node.isIndeterminate) {
      result.push({node, depth})
    } else {
      descend()
    }
  }

  result.sort((a, b) => {
    if (a.depth !== b.depth) return a.depth - b.depth
    return String(a.node.label).localeCompare(String(b.node.label))
  })

  return result.map(({node}) => node)
}

// Returns all selected leaf IDs — used for display/autocomplete matching (options are leaf-only)
export const getCheckedLeafNodeIds = (nodes: CheckboxTreeNode[]): Array<string | number> => {
  const checkedIds: Array<string | number> = []
  const stack = [...nodes]

  while (stack.length) {
    const node = stack.pop()
    if (!node) continue

    const isLeaf = !node.children || node.children.length === 0
    if (node.isSelected && !node.isIndeterminate && isLeaf && !node.isDisabled) {
      checkedIds.push(node.id)
    }

    if (node.children) {
      stack.push(...node.children)
    }
  }

  return checkedIds
}

export const getCheckedNodeIds = (nodes: CheckboxTreeNode[]): Array<string | number> => {
  const checkedIds: Array<string | number> = []
  const stack = [...nodes]

  while (stack.length) {
    const node = stack.pop()
    if (!node) continue

    if (node.isDisabled && node.children) {
      stack.push(...node.children)
      continue
    }

    if (node.isSelected && !node.isIndeterminate) {
      // Fully selected: emit parent id — children are implied
      checkedIds.push(node.id)
    } else if (node.isIndeterminate && node.children) {
      stack.push(...node.children)
    }
  }

  return checkedIds
}

export const mapPlantHierarchyToCheckboxTreeNodes = (
  hierarchy: PlantHierarchy
): CheckboxTreeNode[] => {
  const createNode = (node: PlantHierarchyNode, isRoot = false): CheckboxTreeNode => ({
    id: node.hierarchyId,
    label: node.name,
    isOpen: isRoot,
    isSelected: false,
    isIndeterminate: false,
    isDisabled: !node.isAllowed,
    children: [] as CheckboxTreeNode[]
  })

  const rootHierarchyNode = hierarchy.rootHierarchyNode
  const rootNode = createNode(rootHierarchyNode, true)
  const stack: Array<[PlantHierarchyNode, CheckboxTreeNode]> = [[rootHierarchyNode, rootNode]]

  while (stack.length > 0) {
    const item = stack.pop()
    if (!item) continue

    const [currentHierarchyNode, currentCheckboxNode] = item
    for (const childHierarchyNode of currentHierarchyNode.children) {
      const childCheckboxNode = createNode(childHierarchyNode)
      currentCheckboxNode.children?.push(childCheckboxNode)
      stack.push([childHierarchyNode, childCheckboxNode])
    }
  }

  return [rootNode]
}

export const getAllowedPlantHierarchyIds = (hierarchy: PlantHierarchy): Set<string> => {
  const allowedHierarchyIds = new Set<string>()
  const stack: PlantHierarchyNode[] = [hierarchy.rootHierarchyNode]

  while (stack.length > 0) {
    const currentNode = stack.pop()
    if (!currentNode) continue

    if (currentNode.isAllowed) {
      allowedHierarchyIds.add(currentNode.hierarchyId)
    }

    if (currentNode.children.length > 0) {
      stack.push(...currentNode.children)
    }
  }

  return allowedHierarchyIds
}

export const getMissingPlantHierarchyIds = (
  assignedHierarchyIds: string[],
  allowedHierarchyIds: Set<string>
): string[] => {
  return assignedHierarchyIds.filter((hierarchyId) => !allowedHierarchyIds.has(hierarchyId))
}

export const markSelectedNodesInTree = (
  treeData: CheckboxTreeNode[],
  selectedNodeIds: (string | number)[]
): CheckboxTreeNode[] => {
  if (!selectedNodeIds.length) {
    return treeData
  }

  const selectedNodeIdsSet = new Set(selectedNodeIds)

  const markSelectedNodes = (node: CheckboxTreeNode): CheckboxTreeNode => {
    const updatedNode: CheckboxTreeNode = {
      ...node,
      children: node.children?.map(markSelectedNodes)
    }

    if (selectedNodeIdsSet.has(updatedNode.id) && !updatedNode.isSelected && !updatedNode.isDisabled) {
      return updateNodeAndDescendantsSelection(updatedNode, true)
    }

    return updatedNode
  }

  return treeData.map((node) => recalculateSelectionState(markSelectedNodes(node)))
}

export const clearCheckboxTreeSelection = (treeData: CheckboxTreeNode[]): CheckboxTreeNode[] => {
  const clearSelection = (node: CheckboxTreeNode): CheckboxTreeNode => ({
    ...node,
    isSelected: false,
    isIndeterminate: false,
    children: node.children?.map(clearSelection)
  })

  return treeData.map(clearSelection)
}

const cloneNode = (node: CheckboxTreeNode): CheckboxTreeNode => ({
  ...node,
  children: node.children?.map(cloneNode)
})

const updateNodeAndDescendantsSelection = (
  node: CheckboxTreeNode,
  checked: boolean
): CheckboxTreeNode => ({
  ...node,
  isSelected: node.isDisabled ? false : checked,
  isIndeterminate: false,
  children: node.children?.map((child) => updateNodeAndDescendantsSelection(child, checked))
})

const hasSelectableNodeInSubtree = (node: CheckboxTreeNode): boolean => {
  if (!node.isDisabled) {
    return true
  }

  if (!node.children || node.children.length === 0) {
    return false
  }

  return node.children.some(hasSelectableNodeInSubtree)
}

const recalculateSelectionState = (node: CheckboxTreeNode): CheckboxTreeNode => {
  if (!node.children || node.children.length === 0) {
    return node
  }

  const updatedChildren = node.children.map(recalculateSelectionState)
  const selectableChildren = updatedChildren.filter(hasSelectableNodeInSubtree)

  if (selectableChildren.length === 0) {
    return {
      ...node,
      children: updatedChildren,
      isSelected: false,
      isIndeterminate: false
    }
  }

  const allChildrenSelected = selectableChildren.every(
    (child) => child.isSelected && !child.isIndeterminate
  )
  const hasSelectedChild = selectableChildren.some(
    (child) => child.isSelected || child.isIndeterminate
  )

  return {
    ...node,
    children: updatedChildren,
    isSelected: allChildrenSelected,
    isIndeterminate: hasSelectedChild && !allChildrenSelected
  }
}

const updateSelectionById = (
  node: CheckboxTreeNode,
  nodeId: string | number,
  checked: boolean
): CheckboxTreeNode => {
  if (node.id === nodeId) {
    if (node.isDisabled) {
      return cloneNode(node)
    }

    return updateNodeAndDescendantsSelection(cloneNode(node), checked)
  }

  if (!node.children || node.children.length === 0) {
    return node
  }

  const updatedChildren = node.children.map((child) => updateSelectionById(child, nodeId, checked))

  return recalculateSelectionState({
    ...node,
    children: updatedChildren
  })
}

export const handleCheckboxTreeChange = (
  treeData: CheckboxTreeNode[],
  nodeId: string | number,
  checked: boolean
): CheckboxTreeNode[] => {
  return treeData.map((node) => updateSelectionById(cloneNode(node), nodeId, checked))
}

const updateOpenById = (
  node: CheckboxTreeNode,
  nodeId: string | number,
  isOpen: boolean
): CheckboxTreeNode => {
  if (node.id === nodeId) {
    return {
      ...node,
      isOpen
    }
  }

  return {
    ...node,
    children: node.children?.map((child) => updateOpenById(child, nodeId, isOpen))
  }
}

export const handleCheckboxTreeToggle = (
  treeData: CheckboxTreeNode[],
  nodeId: string | number,
  isOpen: boolean
): CheckboxTreeNode[] => {
  return treeData.map((node) => updateOpenById(node, nodeId, isOpen))
}
